import { RedoOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { getEmails, getLoginCount } from 'api';
import { ButtonTextIcon, LineVertical, Pagination } from 'components';
import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useSetTitlePage from 'hook/useSetTitlePage';
import FormDatePicker from 'page/Application/VisitedPage/component/DatePicker';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ButtonOkStyle } from '../ManageCompanyList/component/Form/styled';
import {
  AccountAccessRanking,
  FileDownloadRanking,
  HeaderListDataLayout,
  LabelSelectProduct,
  LayoutDefault,
  LoginCount,
  TableGroup,
} from './components';
import useMultiLanguageAccountLog from './hook/useMultiLanguageAccountLog';

dayjs.extend(isSameOrAfter);

const AccountLog = () => {
  const { useQueryAPI, useQueryWithoutParams } = useCustomQuery();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();

  const defaultDateFrom = dayjs().subtract(30, 'day');
  const defaultDateTo = dayjs();

  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
  });
  const [filterData, setFilterData] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    email: null,
  });
  const [countToReset, setCountToReset] = useState(0);
  const [email, setEmail] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isAccountsTableLoading, setIsAccountsTableLoading] = useState(false);
  const [isFilesDownloadTableLoading, setIsFilesDownloadTableLoading] =
    useState(false);
  const [emptyText, setEmptyText] = useState('データがありません');

  const { ACCOUNT_LOG_LIST_TITLE } = useMultiLanguageAccountLog();

  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/'}>企業管理</Link>,
      },
      {
        title: ACCOUNT_LOG_LIST_TITLE,
      },
    ];
  }, []);

  const { data: loginCount, isLoading: isLoadingLoginCount } = useQueryAPI({
    api: getLoginCount,
    key: 'getLoginCount',
    params: {
      dateFrom: filterData.dateFrom.format('YYYY-MM-DD'),
      dateTo: filterData.dateTo.format('YYYY-MM-DD'),
      email: filterData?.email,
    },
    enabled: filterData?.email !== null && filterData?.email !== undefined,
  });

  const { data: emails } = useQueryWithoutParams({
    api: getEmails,
    key: 'getEmails',
  });

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPageSize(value);
    } else {
      errorNetWork();
    }
  };

  const handleChangeDateRange = (values) => {
    if (isOnline) {
      if (values && values.length === 2) {
        const dateFrom = values[0] ? values[0] : '';
        const dateTo = values[1] ? values[1] : '';
        if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
          setDateRange({ dateFrom: dateTo, dateTo: dateFrom });
        } else {
          setDateRange({ dateFrom, dateTo });
        }
      } else {
        setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      }
    } else {
      errorNetWork();
    }
  };

  const handleFilterData = () => {
    try {
      if (isOnline) {
        if (email !== null) {
          setFilterData({
            ...dateRange,
            email,
          });
          setEmptyText('データが見つかりませんでした。');
          setCountToReset((prev) => prev + 1);
        }
      } else {
        errorNetWork();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setPageSize(10);
      setCountToReset((prev) => prev + 1);
      setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      setEmail(null);
      setFilterData({
        dateFrom: defaultDateFrom,
        dateTo: defaultDateTo,
        email: null,
      });
      setEmptyText('データがありません');
    } else {
      errorNetWork();
    }
  };

  const handleSelectEmail = (value) => {
    setEmail(value);
  };

  const handleSearch = (value) => {
    if (value && value.length > 256) {
      setSearchText(value.slice(0, 256));
      return value.slice(0, 256);
    }
    setSearchText(value);
    return value;
  };

  useSetTitlePage(`${ACCOUNT_LOG_LIST_TITLE} | Soliton パートナーサイト`, true);

  return (
    <>
      {contextErrorNetWork}
      <Breadcrumb listItem={listItem} titlePage={ACCOUNT_LOG_LIST_TITLE} />
      <LayoutDefault>
        <HeaderListDataLayout
          value={pageSize}
          handleChangePageSize={handleChangePageSize}
          pageSizeOptions={defaultSelectPageSizeOption}
          titleScreen={ACCOUNT_LOG_LIST_TITLE}
          disabled={
            isLoadingLoginCount ||
            isAccountsTableLoading ||
            isFilesDownloadTableLoading
          }
        >
          <>
            <ButtonTextIcon
              text={'リフレッシュ'}
              icon={<RedoOutlined />}
              onClick={handleResetData}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <LabelSelectProduct>メールアドレス</LabelSelectProduct>
            <Select
              showSearch
              placeholder="アカウント名を入力してください。"
              optionFilterProp="label"
              onChange={handleSelectEmail}
              options={
                emails?.data?.map((item) => ({
                  value: item,
                  label: item,
                })) || []
              }
              style={{ width: 300, height: 36 }}
              value={email}
              onSearch={handleSearch}
              searchValue={searchText}
              filterOption={(input, option) => {
                return (
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <FormDatePicker
              width={'360px'}
              height={'36px'}
              dateFormat={DATE_FORMAT_LIST[1]}
              isShowTime={false}
              required={false}
              defaultFromDate={dateRange.dateFrom}
              defaultToDate={dateRange.dateTo}
              resetTrigger={countToReset}
              setDateRange={setDateRange}
              listDisable={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            />
            <LineVertical />
            <ButtonOkStyle
              type="primary"
              htmlType="submit"
              onClick={handleFilterData}
              disabled={
                isLoadingLoginCount ||
                isAccountsTableLoading ||
                isFilesDownloadTableLoading
              }
            >
              {' '}
              検索
            </ButtonOkStyle>
          </>
        </HeaderListDataLayout>
        <LoginCount>
          <span>ログイン数: {loginCount?.data?.toLocaleString() ?? 0}</span>
        </LoginCount>
        <TableGroup>
          <AccountAccessRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            setIsAccountsTableLoading={setIsAccountsTableLoading}
            emptyText={emptyText}
          />
          <FileDownloadRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            setIsFilesDownloadTableLoading={setIsFilesDownloadTableLoading}
            emptyText={emptyText}
          />
        </TableGroup>
        <Pagination
          handleChangePageSize={handleChangePageSize}
          pageSize={pageSize}
          pageSizeOptions={defaultPageSizeOptions}
          isShowTotal={false}
          isShowPagination={false}
        />
      </LayoutDefault>
    </>
  );
};
export default AccountLog;
